<template>
    <div class="support-ticket-detail">
        <div class="detail-header">
            <div class="header-left">
                <button class="button-back" @click="goBack">← BACK</button>
                <div class="header-title">{{ ticketDetail.data ? ticketDetail.data.subject : 'Loading...' }}</div>
            </div>
            <div v-if="ticketDetail.data && ticketDetail.data.status !== 'closed'" class="header-actions">
                <button class="button-close-ticket" @click="closeTicket" :disabled="socketSendLoading !== null">
                    CLOSE TICKET
                </button>
            </div>
        </div>
        
        <div class="detail-content">
            <div v-if="ticketDetail.loading" class="detail-loading">
                <div class="loading-placeholder">
                    <div class="placeholder-line"></div>
                    <div class="placeholder-line"></div>
                </div>
            </div>
            <div v-else-if="ticketDetail.data" class="detail-info">
                <div class="info-row">
                    <span class="info-label">Status:</span>
                    <span class="info-value" :class="'status-' + ticketDetail.data.status">
                        {{ getStatusLabel(ticketDetail.data.status) }}
                    </span>
                </div>
                <div class="info-row">
                    <span class="info-label">Priority:</span>
                    <span class="info-value" :class="'priority-' + ticketDetail.data.priority">
                        {{ ticketDetail.data.priority.toUpperCase() }}
                    </span>
                </div>
                <div class="info-row">
                    <span class="info-label">Created:</span>
                    <span class="info-value">{{ formatTime(ticketDetail.data.createdAt) }}</span>
                </div>
            </div>
            
            <div class="detail-messages">
                <div v-if="ticketDetail.messagesLoading" class="messages-loading">
                    <div class="loading-placeholder">
                        <div class="placeholder-message"></div>
                    </div>
                </div>
                <div v-else-if="ticketDetail.messages.length === 0" class="messages-empty">
                    No messages yet.
                </div>
                <div v-else class="messages-list">
                    <SupportMessageElement 
                        v-for="(message, index) in allMessages" 
                        :key="getMessageKey(message, index)" 
                        :message="message"
                        :is-ticket="true"
                    />
                </div>
            </div>
        </div>
        
        <div v-if="ticketDetail.data && ticketDetail.data.status !== 'closed'" class="detail-footer">
            <div class="footer-input">
                <textarea 
                    v-model="replyMessage" 
                    class="input-textarea" 
                    placeholder="Type your reply..."
                    rows="3"
                    @keydown.enter="handleEnterKey"
                ></textarea>
                <button 
                    class="button-send" 
                    @click="sendReply" 
                    :disabled="!replyMessage.trim() || socketSendLoading !== null"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2L7 9M14 2L10 14L7 9M14 2L2 6L7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SupportMessageElement from '@/components/support/SupportMessageElement';

export default {
    name: 'SupportTicketDetail',
    components: {
        SupportMessageElement
    },
    data() {
        return {
            replyMessage: '',
            pendingMessages: [] // Local pending messages with temporary IDs
        }
    },
    methods: {
        ...mapActions([
            'supportSetView',
            'supportSendMessageSocket',
            'supportCloseTicketSocket',
            'notificationShow'
        ]),
        goBack() {
            this.supportSetView('list');
        },
        handleEnterKey(event) {
            // Shift + Enter = new line (allow default behavior)
            if(event.shiftKey) {
                return;
            }
            
            // Enter = send message (prevent default new line)
            event.preventDefault();
            this.sendReply();
        },
        sendReply() {
            if(!this.replyMessage.trim() || this.socketSendLoading !== null) { return; }
            
            if(!this.ticketDetail.data) { return; }
            
            const messageText = this.replyMessage.trim();
            this.replyMessage = ''; // Clear input immediately for better UX
            
            // Create optimistic message with temporary ID
            const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const optimisticMessage = {
                _id: tempId,
                ticketId: this.ticketDetail.data._id,
                message: messageText,
                sender: {
                    user: this.authUser.user._id,
                    _id: this.authUser.user._id,
                    username: this.authUser.user.username,
                    avatar: this.authUser.user.avatar,
                    rank: this.authUser.user.rank
                },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                pending: true // Mark as pending
            };
            
            // Add to pending messages
            this.pendingMessages.push(optimisticMessage);
            
            // Scroll to bottom after adding pending message
            this.$nextTick(() => {
                this.scrollToBottom();
            });
            
            // Send to server
            this.supportSendMessageSocket({
                ticketId: this.ticketDetail.data._id,
                message: messageText,
                tempId: tempId // Pass temp ID to match with response
            });
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const messagesContainer = this.$el.querySelector('.detail-messages');
                if (messagesContainer) {
                    messagesContainer.scrollTop = messagesContainer.scrollHeight;
                }
            });
        },
        getMessageKey(message, index) {
            // Use content + timestamp as key for stable identification
            // This prevents flicker when pending message becomes confirmed
            const contentKey = `${message.message}-${new Date(message.createdAt).getTime()}`;
            return contentKey + '-' + index;
        },
        closeTicket() {
            if(!this.ticketDetail.data || this.socketSendLoading !== null) { return; }
            
            this.supportCloseTicketSocket({
                ticketId: this.ticketDetail.data._id
            });
        },
        formatTime(timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleString();
        },
        getStatusLabel(status) {
            const labels = {
                'open': 'Open',
                'in-progress': 'In Progress',
                'resolved': 'Resolved',
                'closed': 'Closed'
            };
            return labels[status] || status;
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$el.querySelector('.detail-messages');
                if(container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        }
    },
    computed: {
        ...mapGetters([
            'supportSelectedTicket',
            'supportTicketDetail',
            'socketSendLoading',
            'authUser'
        ]),
        ticketDetail() {
            return this.supportTicketDetail;
        },
        allMessages() {
            // Start with actual messages from server
            const actualMessages = this.ticketDetail.messages || [];
            
            // Create a map of confirmed messages by content+time for quick lookup
            const confirmedMap = new Map();
            actualMessages.forEach(msg => {
                const key = `${msg.message}-${new Date(msg.createdAt).getTime()}`;
                confirmedMap.set(key, msg);
            });
            
            // Process pending messages - replace with confirmed if found, otherwise keep pending
            const processedMessages = this.pendingMessages.map(pending => {
                const key = `${pending.message}-${new Date(pending.createdAt).getTime()}`;
                const confirmed = confirmedMap.get(key);
                
                if (confirmed) {
                    // Replace pending with confirmed message (same element, just update properties)
                    return confirmed;
                }
                return pending; // Keep as pending
            });
            
            // Combine: actual messages that aren't already in pending + processed pending messages
            const pendingKeys = new Set(processedMessages.map(m => `${m.message}-${new Date(m.createdAt).getTime()}`));
            const newActualMessages = actualMessages.filter(msg => {
                const key = `${msg.message}-${new Date(msg.createdAt).getTime()}`;
                return !pendingKeys.has(key);
            });
            
            // Combine and sort
            const combined = [...newActualMessages, ...processedMessages];
            return combined.sort((a, b) => {
                const timeA = new Date(a.createdAt).getTime();
                const timeB = new Date(b.createdAt).getTime();
                return timeA - timeB;
            });
        }
    },
    watch: {
        'ticketDetail.messages': {
            handler(newMessages) {
                if (!newMessages || !Array.isArray(newMessages)) return;
                
                // Update pending messages in place when confirmed
                // Mutate the existing object to prevent Vue from re-rendering
                this.pendingMessages.forEach((pending) => {
                    // Find matching confirmed message
                    const confirmed = newMessages.find(msg => 
                        msg.message === pending.message && 
                        msg.createdAt && 
                        Math.abs(new Date(msg.createdAt).getTime() - new Date(pending.createdAt).getTime()) < 5000
                    );
                    
                    if (confirmed && pending.pending) {
                        // Update properties in place (mutate existing object)
                        // This keeps the same Vue element, just updates its data
                        Object.assign(pending, confirmed);
                        delete pending.pending; // Remove pending flag
                    }
                });
                
                // Remove confirmed messages from pending array
                this.pendingMessages = this.pendingMessages.filter(p => p.pending === true);
                
                // Scroll to bottom when new messages arrive
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            },
            deep: true
        }
    },
    mounted() {
        this.scrollToBottom();
    }
}
</script>

<style scoped>
.support-ticket-detail {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 15px;
    flex: 1;
    min-width: 0;
}

.button-back {
    padding: 6px 12px;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.button-back:hover {
    border-color: var(--accent-yellow);
    color: var(--accent-yellow);
}

.header-title {
    font-size: 16px;
    font-weight: 800;
    color: #ffffff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.header-actions {
    flex-shrink: 0;
}

.button-close-ticket {
    padding: 8px 16px;
    background: rgba(255, 68, 68, 0.2);
    border: 1px solid rgba(255, 68, 68, 0.4);
    border-radius: 6px;
    color: #ff4444;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
}

.button-close-ticket:hover:not(:disabled) {
    background: rgba(255, 68, 68, 0.3);
    border-color: #ff4444;
}

.button-close-ticket:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.detail-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.detail-loading {
    padding: 15px;
}

.loading-placeholder {
    margin-bottom: 10px;
}

.placeholder-line {
    height: 16px;
    background: #2A2A2A;
    border-radius: 4px;
    margin-bottom: 8px;
}

.detail-info {
    padding: 15px;
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-row {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 12px;
}

.info-row:last-child {
    margin-bottom: 0;
}

.info-label {
    color: #80889A;
    font-weight: 600;
    margin-right: 10px;
    min-width: 70px;
}

.info-value {
    color: #ffffff;
    font-weight: 600;
}

.status-open {
    color: #01e1a4;
}

.status-in-progress {
    color: #ffcc00;
}

.status-resolved {
    color: #3b7eb7;
}

.status-closed {
    color: #80889A;
}

.priority-low {
    color: #3b7eb7;
}

.priority-medium {
    color: #ffcc00;
}

.priority-high {
    color: #ff4444;
}

.detail-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px 0;
    background: rgba(0, 0, 0, 0.1);
}

.messages-loading {
    width: 100%;
}

.placeholder-message {
    height: 60px;
    background: #2A2A2A;
    border-radius: 8px;
}

.messages-empty {
    text-align: center;
    color: #80889A;
    font-size: 14px;
    padding: 40px;
}

.messages-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-footer {
    padding: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.2);
}

.footer-input {
    display: flex;
    gap: 10px;
    align-items: flex-end;
}

.input-textarea {
    flex: 1;
    padding: 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #ffffff;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    outline: none;
    transition: all 0.3s ease;
}

.input-textarea:focus {
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--accent-yellow);
}

.input-textarea::placeholder {
    color: #5e768e;
}

.button-send {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--gradient-button-bg);
    border: none;
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.button-send:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(1, 225, 164, 0.3);
}

.button-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.button-send svg {
    width: 16px;
    height: 16px;
}
</style>

